// src/features/MedicalServices/page/Category

import DynamicTable from '@/common/components/DynamicTable/DynamicTable';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import type { CategoryType } from '../../types/medicalServicesTypes';
import { t } from 'i18next';

// 👉 child row type
type SubCategoryType = {
    id: number;
    name: string;
    description: string;
    isActive: boolean;
};

const CategoryPage = () => {

    // ================= MAIN TABLE COLUMNS =================
    const CategoryColumns: ColumnsType<CategoryType> = [
        { title: "Name", dataIndex: "name", key: "name", sorter: true },
        { title: "Description", dataIndex: "description", key: "description" },
        {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: boolean) => (
                <Tag color={isActive ? "green" : "red"}>
                    {isActive ? "Yes" : "No"}
                </Tag>
            ),
        },
    ];

    // ================= CHILD TABLE COLUMNS =================
    const childColumns: ColumnsType<SubCategoryType> = [
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "Description", dataIndex: "description", key: "description" },
        {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: boolean) => (
                <Tag color={isActive ? "green" : "red"}>
                    {isActive ? "Yes" : "No"}
                </Tag>
            ),
        },
    ];

    // ================= EXPANDED ROW (SUBGRID) =================
    const expandedRowRender = (record: CategoryType) => {
        return (
            <div style={{ margin: "0px 25px" }}>
                <DynamicTable<SubCategoryType>
                    key={record.id}
                    tableTitle={t("Sub Services") + ` ${record.name}`}
                    dataApiUrl={API_ENDPOINTS.MEDICALSERVICES.GET_SERVICE_CATEGORY_ITEM}
                    baseQueryKey={["SubServiceCategory", String(record.id)]}
                    columns={childColumns}
                    showActions={false}
                    addNewPermission="false"
                    extraParameters={{ categoryId: record.id }}

                    // ✅ SERVER-SIDE PAGINATION: 5 rows per page
                    pagination={{
                        pageSize: 5,              // 5 records per page
                        showSizeChanger: false,   // disable page size change
                        backendPagination: true,  // important: fetch pages from backend
                    }}

                    filterConfigs={[
                        {
                            field: "name",
                            type: "input",
                            placeholder: "Search sub service",
                        }
                    ]}
                />
            </div>
        );
    };

    return (
        <div>
            <DynamicTable<CategoryType>
                dataApiUrl={API_ENDPOINTS.MEDICALSERVICES.SEARCH_SERVICECATEGORY}
                baseQueryKey={QUERY_KEYS.MEDICALSERVICES.SERVICECATEGORY}
                columns={CategoryColumns}
                tableTitle={"Main Services"}
                showActions={true}
                deleteBaseUrl={API_ENDPOINTS.MEDICALSERVICES.DELETE_SERVICECATEGORY}
                deletePayloadField="name"

                // ✅ SUBGRID ATTACHED
                expandable={{
                    expandedRowRender,
                    rowExpandable: (record) => record.id !== undefined,
                }}

                filterConfigs={[
                    { field: "name", placeholder: "Search by category" },
                    {
                        field: "Print",
                        type: "export",
                        exportOptions: {
                            url: API_ENDPOINTS.MEDICALSERVICES.EXPORT_SERVICECATEGORY,
                            fileName: "ServiceCategories",
                            fileType: "excel",
                            defaultPayload: {},
                            columns: [
                                { header: "Name", key: "name" },
                                { header: "Arabic Name", key: "nameAr" },
                                { header: "Description", key: "description" },
                                { header: "Description (Arabic)", key: "descriptionAr" },
                                {
                                    header: "Active",
                                    key: "isActive",
                                    transform: (val: boolean) => val ? "Yes" : "No",
                                },
                            ],
                        },
                    },
                ]}
            />
        </div>
    );
};

export default CategoryPage;